import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import type { ISupportReportOverview } from '../../models/Support/ISupportReportOverview';
import { supportQueryRetry } from '../../utils/Support/supportHelpers';
import { SUPPORT_PATHS, SUPPORT_QUERY_KEYS, type SupportReportOverviewParams } from './supportQueryHelpers';

interface UseSupportReportOverviewArgs {
	/** null requests the admin-gated all-mailboxes overview; gating the view is the caller's job. */
	mailboxId: string | null;
	/** ISO-8601 UTC instant, inclusive start of the reporting window. */
	start: string;
	/** ISO-8601 UTC instant, exclusive end of the reporting window. */
	end: string;
	/** IANA timezone the API buckets the daily series in. */
	timezone: string;
}

export function useSupportReportOverviewQuery({ mailboxId, start, end, timezone }: UseSupportReportOverviewArgs) {
	const userSession = useUserSession();
	const params: SupportReportOverviewParams = { start, end, timezone };

	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.reports.overview(mailboxId, params),
		staleTime: 30_000,
		retry: supportQueryRetry,
		// Hold the previous range's data while a new range loads so the dashboard doesn't flash skeletons.
		placeholderData: previous => previous,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<ISupportReportOverview>(
				SUPPORT_PATHS.reports.overview(mailboxId, params),
				'GET'
			),
		// '' means no mailbox has settled yet (list still loading); null is a deliberate all-mailboxes request.
		enabled: mailboxId !== '' && !!start && !!end,
		meta: { toastErrorMessage: 'Failed to load support reports' },
	});
}
